import {
  getBomItemsBatchRefreshAPI,
  getBomStatusAPI,
  saveBomDistributorsAPI,
} from "actions/bomActions";
import PageWrapper from "components/PageWrapper";
import BomStatsView from "components/bom/v2/BomStatsView";
import BomItemsTable from "components/bom/v2/BomItemsTable";
import {
  Download,
  Plus,
  BarChart2,
  FileSpreadsheet,
  ArrowLeft,
  RefreshCcw,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useParams, useNavigate } from "react-router-dom";
import {
  bomToolStats,
  getOptimalBomItemResults,
  getPartialQtyCount,
  buildBomExcelData,
  buildBomExcelBuffer,
  getEffectivePriceSlab,
} from "utils/bomToolUtils";
import { useAuth } from "AuthContext";
import LoadingComponent from "components/LoadingComponent";
import { addBomItemAPI } from "actions/bomItemActions";
import { getLastOrderedByPartsAPI } from "actions/ordersAction";
import SamiSpin from "components/SamiSpin";
const MySwal = withReactContent(Swal);

// Status polling while the BOM is still processing. A failed status call
// (timeout, API restart) retries with backoff instead of ending the loop.
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_BACKOFF_MS = 60000;

// Parse the persisted per-BOM distributor filter into an array of names (or null
// = all distributors). Tolerates a JSON string, an array, or empty/invalid input.
const parseSavedDistributors = (raw) => {
  if (raw == null || raw === "") return null;
  try {
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(arr) && arr.length > 0 ? arr : null;
  } catch {
    return null;
  }
};

const BomToolV2Page = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useAuth();
  const [all, setAll] = useState({
    loading: true,
    bomProcessingStatus: "pending",
    isFetching: true,
  });
  const [isRetryingFailed, setIsRetryingFailed] = useState(false);
  const [productOverrides, setProductOverrides] = useState({});
  // null = all distributors considered (no filter). Once the user toggles, this
  // becomes a Set of the distributor names to include.
  const [selectedDistributors, setSelectedDistributors] = useState(null);
  // Whether the saved filter from the backend has been applied to state yet.
  const [distHydrated, setDistHydrated] = useState(false);
  // Map of mfr_part_number → { ordered_at, distributor, order_id, qty } for
  // the "recently ordered" hint under each part number. Empty until the lookup
  // resolves.
  const [lastOrderedMap, setLastOrderedMap] = useState({});
  // Price column basis, shared with the table's "Price: Stock Qty / RFQ Qty"
  // toggle. false = Stock Qty (each product's stock-capped selectedPrice slab),
  // true = RFQ Qty (the slab the full requested qty qualifies for). Lifted here
  // so the Raw data / consolidated downloads reflect the same basis on screen.
  const [priceByRfqQty, setPriceByRfqQty] = useState(false);

  // Polling state lives in refs: the timers outlive the render that set them.
  // activeIdRef is the BOM this page shows. A status call can take seconds,
  // so a response for a BOM the page has left is dropped rather than rendered.
  const activeIdRef = useRef(id);
  const inFlightIdRef = useRef(null);
  const reloadQueuedRef = useRef(false);
  const pollTimerRef = useRef(null);
  const pollFailuresRef = useRef(0);

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const schedulePoll = (forId, delayMs) => {
    clearPollTimer();
    pollTimerRef.current = setTimeout(() => {
      pollTimerRef.current = null;
      onLoad(forId);
    }, delayMs);
  };

  const handleDistributorChange = (itemId, newProduct) => {
    setProductOverrides((prev) => ({ ...prev, [itemId]: newProduct }));
  };

  // Every distributor that appears anywhere in the BOM response.
  const distributors = useMemo(() => {
    const set = new Set();
    for (const item of all.rawBomItems || [])
      for (const pd of item.processing_data || [])
        if (pd.distributor) set.add(pd.distributor);
    return [...set];
  }, [all.rawBomItems]);

  const isDistributorSelected = (d) =>
    selectedDistributors === null || selectedDistributors.has(d);

  // Update state and persist the filter to the BOM record so it is restored on
  // reopen. `next` is a Set, or null for "all distributors".
  const persistDistributors = (next) => {
    setSelectedDistributors(next);
    if (all.bomId) {
      saveBomDistributorsAPI({
        bom_id: all.bomId,
        selected_distributors: next === null ? null : [...next],
      });
    }
  };

  const toggleDistributor = (d) => {
    const next =
      selectedDistributors === null
        ? new Set(distributors)
        : new Set(selectedDistributors);
    if (next.has(d)) {
      // Always keep at least one distributor selected.
      if (next.size <= 1) return;
      next.delete(d);
    } else {
      next.add(d);
    }
    persistDistributors(next);
  };

  // Re-run the optimal selection against only the selected distributors.
  const bomItems = useMemo(
    () => getOptimalBomItemResults(all.rawBomItems || [], selectedDistributors),
    [all.rawBomItems, selectedDistributors],
  );

  // The product to treat as "chosen" for an item: a change made this session
  // wins, then the persisted saved distributor (if it is still a valid option),
  // otherwise the computed optimal.
  const resolveSelectedProduct = useCallback(
    (item) => {
      if (productOverrides[item.id]) return productOverrides[item.id];
      const savedKey = item.savedDistributorKey;
      if (savedKey) {
        // Older saved values used a "distributor||packageType" key; fall back
        // to matching just the distributor portion for those.
        const legacyDist = savedKey.split("||")[0];
        const opt = (item.distributorOptions || []).find(
          (o) => o.key === savedKey || o.key === legacyDist,
        );
        if (opt?.product) return opt.product;
      }
      return item.optimal;
    },
    [productOverrides],
  );

  // Apply the chosen product so stats, summaries and downloads all reflect the
  // saved/selected distributor — not just the table dropdown.
  const effectiveBomItems = useMemo(
    () =>
      bomItems.map((item) => {
        const selected = resolveSelectedProduct(item);
        return selected ? { ...item, optimal: selected } : item;
      }),
    [bomItems, resolveSelectedProduct],
  );

  const excelData = useMemo(
    () =>
      buildBomExcelData(all.rawBomItems || [], effectiveBomItems, priceByRfqQty),
    [all.rawBomItems, effectiveBomItems, priceByRfqQty],
  );

  const bomStats = useMemo(
    () =>
      all.bomStatsBase
        ? {
            ...all.bomStatsBase,
            partialQtyCount: getPartialQtyCount(effectiveBomItems),
          }
        : undefined,
    [all.bomStatsBase, effectiveBomItems],
  );

  const lastModified = (() => {
    if (!all.bomUpdatedAt) return null;
    const d = new Date(all.bomUpdatedAt);
    return isNaN(d.getTime())
      ? null
      : d.toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        });
  })();

  // Seed the filter from the saved value once, after the BOM first loads.
  useEffect(() => {
    if (distHydrated || all.savedDistributors === undefined) return;
    const saved = all.savedDistributors; // array of names, or null = all
    setSelectedDistributors(saved ? new Set(saved) : null);
    setDistHydrated(true);
  }, [all.savedDistributors, distHydrated]);

  const handleRetryFailedFetch = async () => {
    const ids = bomStats?.failedFetchItemIds || [];
    if (ids.length === 0) return;
    if (isRetryingFailed) return;

    setIsRetryingFailed(true);

    try {
      const res = await getBomItemsBatchRefreshAPI({ bomIds: ids });
      if (res.status) {
        MySwal.fire({
          icon: "success",
          title: "Retry started",
          text: `Reprocessing ${ids.length} item(s).`,
          timer: 2500,
          showConfirmButton: false,
        });
        setTimeout(() => {
          onLoad(id);
        }, 2500);
      } else {
        MySwal.fire({
          icon: "error",
          title: "Retry failed",
          text: res.message || "Unable to retry failed items.",
        });
      }
    } catch (e) {
      MySwal.fire({
        icon: "error",
        title: "Retry failed",
        text: "Something went wrong, please try again later.",
      });
    } finally {
      setIsRetryingFailed(false);
    }
  };

  const onLoad = async (forId = activeIdRef.current) => {
    if (!forId || forId !== activeIdRef.current) return;
    // One status call per BOM at a time. A reload asked for meanwhile (a
    // retry, an added item) runs as soon as this call returns, so it shows
    // the change instead of racing this call or being dropped.
    if (inFlightIdRef.current === forId) {
      reloadQueuedRef.current = true;
      return;
    }
    inFlightIdRef.current = forId;
    clearPollTimer();
    setAll((prev) => ({ ...prev, isFetching: true }));

    let res;
    try {
      res = await getBomStatusAPI(forId);
    } catch (e) {
      res = { status: false };
    } finally {
      if (inFlightIdRef.current === forId) inFlightIdRef.current = null;
    }
    if (forId !== activeIdRef.current) return;
    console.log("BOM TOOL V2 Response----->", { res });

    const reloadQueued = reloadQueuedRef.current;
    reloadQueuedRef.current = false;

    if (res?.status !== true) {
      pollFailuresRef.current += 1;
      setAll((prev) => ({ ...prev, isFetching: false }));
      schedulePoll(
        forId,
        reloadQueued
          ? 0
          : Math.min(POLL_INTERVAL_MS * 2 ** pollFailuresRef.current, POLL_MAX_BACKOFF_MS),
      );
      return;
    }
    pollFailuresRef.current = 0;

    const rawBomItems = res.data?.bom_items || [];

    const failedFetchItemIds = rawBomItems
      .filter((item) =>
        (item.processing_data || []).some((pd) => pd?.status === "failed"),
      )
      .map((item) => item.id);

    // Filter-independent stats. The partial-qty count, optimal results and
    // Excel data are derived reactively from rawBomItems + selectedDistributors.
    const bomStatsBase = {
      ...bomToolStats(res),
      failedFetchCount: failedFetchItemIds.length,
      failedFetchItemIds,
    };
    const isDone = bomStatsBase.totalBomCount === bomStatsBase.completedBomCount;

    // Functional update: this may run from a timer set several renders ago,
    // and spreading that render's `all` would undo anything changed since.
    setAll((prev) => ({
      ...prev,
      bomStatsBase,
      rawBomItems,
      loading: false,
      bomId: res.data.bom.id,
      bomFileName: res.data.bom?.file_name || "",
      bomUpdatedAt: res.data.bom?.updated_at ?? null,
      savedDistributors: parseSavedDistributors(
        res.data.bom?.selected_distributors,
      ),
      bomProcessingStatus: isDone ? "completed" : "pending",
      isFetching: false,
    }));

    if (reloadQueued) schedulePoll(forId, 0);
    else if (!isDone) schedulePoll(forId, POLL_INTERVAL_MS);

    fetchLastOrdered(rawBomItems);
  };

  // Pull the most recent order per mfr_part_number for everything in this BOM
  // so the table can show a "recently ordered on ..." note under each row.
  // Fails silently — the note is purely informational.
  const fetchLastOrdered = async (rawBomItems) => {
    const mfrParts = [
      ...new Set(
        (rawBomItems || [])
          .flatMap((item) => [
            item?.part_number,
            ...(item?.processing_data || []).flatMap((pd) =>
              (pd?.products || []).map((p) => p?.mfr_part_number),
            ),
          ])
          .filter(Boolean),
      ),
    ];
    if (mfrParts.length === 0) {
      setLastOrderedMap({});
      return;
    }
    try {
      const res = await getLastOrderedByPartsAPI(mfrParts);
      if (res?.status && res?.data && typeof res.data === "object") {
        setLastOrderedMap(res.data);
      }
    } catch (e) {
      // non-blocking
    }
  };

  const handleConsolidatedDownload = () => {
    const items = bomItems || [];
    const headerRow = [
      "SKU",
      "Make",
      "Distributor",
      "Description",
      "HS Code",
      "Date Code",
      "RFQ QTY",
      "Stock",
      "MOQ",
      "MUL",
      "Price",
      "Split Details",
      "Status",
    ];
    const dataRows = items.map((item) => {
      // Use the session/saved selected distributor, otherwise the computed optimal
      const p = resolveSelectedProduct(item);
      if (!p)
        return [
          item.part_number,
          "",
          "",
          "",
          "",
          "",
          item.qty,
          "",
          "",
          "",
          "",
          item.itemStatus,
          "",
        ];
      const displayStock = p.displayAvailability ?? p.availability;
      // Match the table's Price column basis (Stock Qty vs RFQ Qty toggle).
      const priceSlab = getEffectivePriceSlab(
        p,
        parseInt(item.qty, 10) || 0,
        priceByRfqQty,
      );
      return [
        item.part_number,
        p.manufacturer ?? "",
        p.distributor ?? "",
        p.description ?? "",
        p.hts_code ?? "",
        p.date_code ?? "",
        item.qty,
        displayStock ?? "",
        priceSlab?.min_qty ?? "",
        p.isSplitOrder ? "Split" : (p.price?.multiple ?? ""),
        priceSlab?.price ?? "",
        (() => {
          if (!p.isSplitOrder) return "";
          const t1 = p.price?.package_type?.split("+")[0] ?? "TR";
          const t2 = p.price?.package_type?.split("+")[1] ?? "CT";
          return `${p.splitFirst?.qty?.toLocaleString()} ${t1} @ ${p.splitFirst?.unitPrice} + ${p.splitSecond?.qty?.toLocaleString()} ${t2} @ ${p.splitSecond?.unitPrice}`;
        })(),
        (() => {
          const avail =
            parseInt(String(displayStock ?? 0).replace(/\D/g, ""), 10) || 0;
          const rfq = parseInt(item.qty, 10) || 0;
          const partialNote =
            avail > 0 && avail < rfq ? ` | Partial (${avail} in stock)` : "";

          // Note when the selected distributor differs from the computed optimal
          const isDistributorChanged =
            p && item.optimal && p.distributor !== item.optimal.distributor;
          const distributorChangeNote = isDistributorChanged
            ? ` | distributor changed from ${item.optimal?.distributor || "unknown"} to ${p.distributor || "unknown"}`
            : "";

          return `${item.itemStatus ?? ""}${partialNote}${distributorChangeNote}`;
        })(),
      ];
    });
    const csvContent = [headerRow, ...dataRows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const link = document.createElement("a");
    link.setAttribute(
      "href",
      `data:text/csv;charset=utf-8,${encodeURIComponent(csvContent)}`,
    );
    const baseName = (all.bomFileName || "")
      .replace(/\.[^/.]+$/, "")
      .replace(/\s+/g, "-");
    const downloadName = baseName
      ? `bom-consolidated_${baseName}.csv`
      : "bom-consolidated.csv";
    link.setAttribute("download", downloadName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRawDataExcelDownload = async () => {
    const buffer = await buildBomExcelBuffer(excelData);
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bom-price-data.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleAddToCart = (item, silent = false) => {
    console.log("Add to cart----->", {
      ...item.optimal,
      required_qty: item.qty,
    });
    addToCart({
      ...item.optimal,
      bom_item_id: item.id,
      bom_id: all.bomId,
      required_qty: parseInt(item.qty),
      qty: parseInt(item.qty),
      // For split orders, `optimal.availability` is a sufficiency flag (the
      // full required qty if both packaging components cover it, else a hard
      // 0) rather than real stock — use the true on-hand count so the cart
      // shows the same number as the BOM table and the distributor's site.
      availability: parseInt(
        item.optimal.displayAvailability ?? item.optimal.availability,
      ),
    });

    if (silent) return;

    MySwal.fire({
      title: <strong>Cart Update!</strong>,
      html: (
        <i>
          You've added {item?.optimal?.part_number ?? item?.part_number}{" "}
          (Qty: {parseInt(item.qty).toLocaleString()}) to cart{" "}
        </i>
      ),
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "View Cart",
      cancelButtonText: "Continue Shopping",
      confirmButtonColor: "#5F47C9",
      cancelButtonColor: "#6b7280",
    }).then((result) => {
      if (result.isConfirmed) navigate("/cart");
    });
  };

  const handleAddNewItem = async () => {
    if (all.bomProcessingStatus == "pending") {
      alert("Adding new item is allowed after the BOM processing is completed");
      return;
    }
    const { value: formValues } = await MySwal.fire({
      title: "Add New BOM Item",
      html: `
        <div style="text-align: left;">
          <label style="display: block; margin-bottom: 8px; font-weight: 500;">Part Number</label>
          <input id="swal-input-part-number" class="swal2-input" placeholder="Enter part number" style="width: 90%; margin: 0 0 16px 0;">
          <label style="display: block; margin-bottom: 8px; font-weight: 500;">Quantity</label>
          <input id="swal-input-qty" type="number" class="swal2-input" placeholder="Enter quantity" style="width: 90%; margin: 0;">
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Add Item",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const partNumber = document.getElementById(
          "swal-input-part-number",
        ).value;
        const qty = document.getElementById("swal-input-qty").value;

        if (!partNumber || !qty) {
          Swal.showValidationMessage("Please fill in all fields");
          return false;
        }

        if (parseInt(qty) <= 0) {
          Swal.showValidationMessage("Quantity must be greater than 0");
          return false;
        }

        return { part_number: partNumber, qty: parseInt(qty) };
      },
    });

    if (formValues) {
      console.log("Add new BOM item:", formValues);
      // TODO: Call API to add new BOM item
      const payload = {
        ...formValues,
        bom_id: all.bomId,
      };
      await addBomItemAPI(payload);
      onLoad(id);
      MySwal.fire({
        title: "Success!",
        text: `${formValues.part_number} will be added to the BOM`,
        icon: "info",
      });
    }
  };

  // Runs again when the route moves to another BOM in the same page instance:
  // start that BOM from a clean slate, and stop the previous BOM's polling.
  useEffect(() => {
    activeIdRef.current = id;
    reloadQueuedRef.current = false;
    pollFailuresRef.current = 0;
    setAll({ loading: true, bomProcessingStatus: "pending", isFetching: true });
    setProductOverrides({});
    setSelectedDistributors(null);
    setDistHydrated(false);
    setLastOrderedMap({});
    onLoad(id);
    return () => {
      activeIdRef.current = null;
      clearPollTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  console.log({ all });

  const isComplete = all.bomProcessingStatus === "completed";
  const total = bomStats?.totalBomCount ?? 0;
  const completed = bomStats?.completedBomCount ?? 0;
  const processing = bomStats?.processingBomCount ?? 0;
  const pending = bomStats?.pendingBomCount ?? 0;
  const enrichedPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <PageWrapper>
      {all.loading && <LoadingComponent />}
      {!all.loading && (
        <div className="flex flex-col gap-4">
          {/* Top strip */}
          <div className="bg-white rounded-md shadow-sm px-[22px] py-[18px] grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-[18px] items-center">
            <span className="w-11 h-11 rounded-lg bg-s360-violet-50 text-s360-violet inline-flex items-center justify-center shrink-0">
              <FileSpreadsheet className="w-5 h-5" />
            </span>
            <div className="flex flex-col gap-1 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-xs text-s360-fg3 hover:text-s360-violet w-fit"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Uploaded BOM
              </button>
              <h1 className="font-mono text-[20px] leading-7 font-bold text-s360-ink truncate">
                {all.bomFileName || "BOM Tool"}
              </h1>
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                    isComplete
                      ? "bg-s360-success-bg text-s360-success-ink"
                      : "bg-s360-warning-bg text-s360-warning-ink"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      isComplete ? "bg-s360-success" : "bg-s360-warning"
                    }`}
                  />
                  {isComplete ? "Analysis complete" : "Analyzing…"}
                </span>
                <span className="text-xs text-s360-fg3">
                  <strong className="text-s360-fg1 font-semibold">{total}</strong> parts
                </span>
                {lastModified && (
                  <span className="text-xs text-s360-fg3">updated {lastModified}</span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-stretch lg:items-end gap-2">
              {/* Enriched progress */}
              <div className="flex items-center gap-2.5">
                <span className="h-1.5 w-32 rounded-full bg-s360-bg2 overflow-hidden">
                  <span
                    className="block h-full bg-s360-violet transition-all"
                    style={{ width: `${enrichedPct}%` }}
                  />
                </span>
                <span className="text-xs text-s360-fg3 whitespace-nowrap">
                  <strong className="text-s360-fg1">{completed}</strong>/{total} enriched
                  {processing > 0 && <span className="text-s360-violet"> · {processing} processing</span>}
                  {pending > 0 && <> · {pending} pending</>}
                </span>
              </div>
              <div className="flex flex-wrap lg:justify-end items-center gap-2">
                {bomStats?.failedFetchCount > 0 && (
                  <button
                    onClick={handleRetryFailedFetch}
                    disabled={isRetryingFailed}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-s360-danger-bg text-[#700A14] text-[13px] font-semibold hover:bg-[#fbd5d9] disabled:opacity-60"
                  >
                    {isRetryingFailed ? (
                      <SamiSpin className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCcw className="w-3.5 h-3.5" />
                    )}
                    Retry {bomStats.failedFetchCount} failed
                  </button>
                )}
                <button
                  onClick={handleAddNewItem}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
                >
                  <Plus className="w-3.5 h-3.5" /> Add item
                </button>
                <button
                  onClick={() => navigate(`/bom-analysis/${id}`)}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
                >
                  <BarChart2 className="w-3.5 h-3.5" /> BOM analysis
                </button>
                <button
                  onClick={handleRawDataExcelDownload}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
                >
                  <Download className="w-3.5 h-3.5" /> Raw data
                </button>
                <button
                  onClick={handleConsolidatedDownload}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
              </div>
            </div>
          </div>

          <BomStatsView
            bomStats={bomStats}
            bomItems={effectiveBomItems}
            distributorOnlySummary={excelData?.distributorSummary ?? []}
            isDistributorSelected={isDistributorSelected}
            toggleDistributor={toggleDistributor}
          />

          {all.isFetching && (
            <span className="inline-flex items-center gap-1.5 text-xs text-s360-violet">
              <SamiSpin className="w-3.5 h-3.5 animate-spin" /> Fetching BOM data…
            </span>
          )}

          <BomItemsTable
            bomItems={bomItems}
            totalCount={bomStats?.totalBomCount}
            onAddToCart={handleAddToCart}
            bomProcessingStatus={all.bomProcessingStatus}
            onLoad={onLoad}
            onDistributorChange={handleDistributorChange}
            lastOrderedMap={lastOrderedMap}
            priceByRfqQty={priceByRfqQty}
            setPriceByRfqQty={setPriceByRfqQty}
          />
        </div>
      )}
    </PageWrapper>
  );
};

export default BomToolV2Page;
